import * as React from 'react'
import { LucideIcon } from 'utils/lucideIcon'
import './searchInput.scss'

interface SearchInputProps {
    inputRef: React.RefObject<HTMLInputElement>
    value: string
    // Rotating hint texts shown while the input is empty; cycles with a
    // left-to-right shimmer wipe.
    hints: string[]
    // Omitted while the results list is not rendered (empty idle state).
    listboxId?: string
    activeDescendantId?: string
    onChange: (value: string) => void
    onKeyDown: (event: React.KeyboardEvent) => void
}

const HINT_INTERVAL_MS = 5000
const HINT_WIPE_MS = 600

export function SearchInput({
    inputRef,
    value,
    hints,
    listboxId,
    activeDescendantId,
    onChange,
    onKeyDown,
}: SearchInputProps): JSX.Element {
    const [hintIndex, setHintIndex] = React.useState(0)
    const [isWiping, setIsWiping] = React.useState(false)
    const isEmpty = value === ''

    // Kick off a wipe every interval while the hint is visible.
    React.useEffect(() => {
        if (!isEmpty || hints.length < 2) {
            return
        }
        const interval = window.setInterval(() => setIsWiping(true), HINT_INTERVAL_MS)
        return () => window.clearInterval(interval)
    }, [isEmpty, hints.length])

    // Commit the next hint once the wipe has fully covered the old one.
    React.useEffect(() => {
        if (!isWiping) {
            return
        }
        const timeout = window.setTimeout(() => {
            setHintIndex((index) => (index + 1) % hints.length)
            setIsWiping(false)
        }, HINT_WIPE_MS)
        return () => window.clearTimeout(timeout)
    }, [isWiping, hints.length])

    const nextHint = hints[(hintIndex + 1) % hints.length]

    return (
        <div className="global-search-input-row">
            <LucideIcon iconName="Search" size={18} className="global-search-input-icon" />
            <div className="global-search-input-field">
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={onKeyDown}
                    aria-label="Search"
                    aria-describedby="global-search-syntax-tip"
                    role="combobox"
                    aria-expanded={Boolean(listboxId)}
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-activedescendant={activeDescendantId}
                    autoComplete="off"
                    spellCheck={false}
                />
                {/* Static, screen-reader-only equivalent of the rotating visual hints. */}
                <span id="global-search-syntax-tip" className="global-search-sr-only">
                    Search records by name, email, order number or barcode. You can also type queries like &ldquo;orders
                    canceled last month&rdquo; or &ldquo;members overdue&rdquo; to open filtered views.
                </span>
                {isEmpty && hints.length > 0 && (
                    <div className="global-search-hint" aria-hidden="true">
                        <span className={`global-search-hint-text${isWiping ? ' is-wiping' : ''}`}>
                            {hints[hintIndex]}
                        </span>
                        {isWiping && (
                            <>
                                <span className="global-search-hint-text is-next">{nextHint}</span>
                                <span className="global-search-hint-streak" />
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
